import { getContrastColor } from './TagSelect';
import React, { useState, useRef, useEffect } from 'react';
import { components, MultiValueGenericProps } from 'react-select';
import TagsModal from './TagsModal';
import { FiEdit } from 'react-icons/fi';

interface EditableMultiValueLabelProps {
  onTagRename: (tagId: string, newName: string, color?: string) => void;
  onDelete: (tagId: string) => void;
  props: MultiValueGenericProps;
}

const SimpleEditableMultiValueLabel = ({ onTagRename, onDelete, props }: EditableMultiValueLabelProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(props.children);
  const inputRef = useRef<HTMLInputElement>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);


  const handleClick = (e: React.MouseEvent) => {
    if (e.button == 0) {
      e.stopPropagation();
      setIsEditing(true);
    } else if (e.button == 2) {
      e.stopPropagation();
      setShouldRender(true);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== props.children && onTagRename) {
      onTagRename(props.data.value, trimmedValue);
    }
    setEditValue(props.children.toString());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setEditValue(props.children.toString());
      setIsEditing(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        size={Math.max(editValue.length, 4)}
        style={{
          display: 'inline',
          outline: 'none',
          outlineOffset: '-1px',
          borderRadius: '2px',
          color: getContrastColor(props.data.color || '#e9ecef'), fontSize: '14px',
          fontFamily: 'inherit',
          padding: '1px 6px',
          margin: '-1px -2px',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          cursor: 'text',
          minWidth: '40px',
          lineHeight: 'inherit',
          verticalAlign: 'baseline',
        }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <>
      {shouldRender && (
        <TagsModal onSave={onTagRename} onDelete={onDelete} onClose={() => setShouldRender(false)} isOpen={shouldRender} tag={props.data} />
      )}

      <button
        onMouseDown={handleClick}
        style={{ background: 'none', border: 'none', padding: '0', margin: '0', cursor: 'pointer' }}
      >
        <components.MultiValueLabel {...props} />
      </button>

      <div
        className='tag-edit'
        style={{ color: getContrastColor(props.data.color || '#e9ecef') }}
        onClick={(e) => {
          e.stopPropagation();
          setShouldRender(true);
        }}>
        <FiEdit size={12} />
      </div>
    </>
  );
};

export default SimpleEditableMultiValueLabel;
